import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui'
import './ListViews.scss'

export const DEFAULT_VIEW_NAME = 'Padrão'
const NAME_MAX_LENGTH = 60

/** Dialog asking the name of a view (create, "save as" and rename). */
export const ViewNameDialog = ({
  open,
  title,
  initialName = '',
  onSubmit,
  onClose,
}) => {
  const [name, setName] = useState(initialName)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setName(initialName)
      setSaving(false)
    }
  }, [open, initialName])

  const submit = async (event) => {
    event.preventDefault()
    if (!name.trim() || saving) return
    setSaving(true)
    try {
      await onSubmit(name.trim())
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent size="sm">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <label htmlFor="list-view-name" className="tw-text-sm">
              Nome da visão
            </label>
            <input
              id="list-view-name"
              className="form-control"
              autoFocus
              maxLength={NAME_MAX_LENGTH}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Minhas ordens abertas"
            />
            <small className="text-muted">
              Salva os filtros, a ordenação e as colunas atuais. A pesquisa por
              termo vale para todas as visões. Só você vê suas visões.
            </small>
          </DialogBody>
          <DialogFooter>
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              type="submit"
              color="primary"
              disabled={!name.trim() || saving}
            >
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const ViewTabMenu = ({ onRename, onDelete }) => {
  const ref = useRef(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return undefined
    const handle = (event) =>
      ref.current && !ref.current.contains(event.target) && setOpen(false)
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  return (
    <span className="filter-pill-wrapper" ref={ref}>
      <span
        role="button"
        tabIndex={0}
        aria-label="Opções da visão"
        className="list-view-tab-menu"
        onClick={(e) => {
          e.stopPropagation()
          setOpen(!open)
        }}
        onKeyDown={(e) => {
          e.stopPropagation()
          e.key === 'Enter' && setOpen(!open)
        }}
      >
        ⋯
      </span>
      {open && (
        <div
          className="filter-popover"
          style={{ width: 160, fontWeight: 'normal' }}
        >
          <button
            type="button"
            className="filter-field-option"
            onClick={(e) => {
              e.stopPropagation()
              setOpen(false)
              onRename()
            }}
          >
            Renomear
          </button>
          <button
            type="button"
            className="filter-field-option text-danger"
            onClick={(e) => {
              e.stopPropagation()
              setOpen(false)
              onDelete()
            }}
          >
            Excluir
          </button>
        </div>
      )}
    </span>
  )
}

/**
 * Tabs with the built-in "Padrão" view and the user's saved views of a
 * list, plus the actions to save the current filters as a view.
 */
const SavedViewsTabs = ({
  views = [],
  activeViewId,
  isDirty,
  onSelect,
  onSaveNew,
  onSave,
  onDiscard,
  onRename,
  onDelete,
}) => {
  const activeView = views.find((view) => view.id === activeViewId)

  return (
    <div className="list-views-tabs" data-testid="list-views-tabs">
      <button
        type="button"
        className={`list-view-tab ${!activeView ? 'active' : ''}`}
        onClick={() => onSelect(null)}
      >
        {DEFAULT_VIEW_NAME}
      </button>
      {views.map((view) => (
        // Not a <button>: the active tab holds the options menu buttons
        <div
          role="button"
          tabIndex={0}
          key={view.id}
          className={`list-view-tab ${
            view.id === activeViewId ? 'active' : ''
          }`}
          onClick={() => view.id !== activeViewId && onSelect(view)}
          onKeyDown={(e) =>
            e.key === 'Enter' && view.id !== activeViewId && onSelect(view)
          }
        >
          {view.name}
          {view.id === activeViewId && (
            <ViewTabMenu
              onRename={() => onRename(view)}
              onDelete={() => onDelete(view)}
            />
          )}
        </div>
      ))}
      <button
        type="button"
        className="list-view-tab list-view-add"
        onClick={onSaveNew}
      >
        + Salvar visão
      </button>
      {isDirty && (
        <span className="list-view-dirty" data-testid="list-view-dirty">
          • alterada
          {activeView && (
            <button type="button" onClick={onSave}>
              Salvar
            </button>
          )}
          <button type="button" onClick={onSaveNew}>
            Salvar como nova
          </button>
          <button type="button" onClick={onDiscard}>
            Descartar
          </button>
        </span>
      )}
    </div>
  )
}

export default SavedViewsTabs
